import React from 'react';
import { useTranslation } from 'react-i18next';
import { TechnologyDetail } from '../../../types/interfaces';

interface URL {
  id: number;
  url: string;
}

interface TechnologiesProps {
  selectedUrl: string | null;
  urls: URL[];
  onUrlChange: (url: string) => void;
  // Se espera que esté agrupado por categoryName
  technologiesData: { [categoryName: string]: TechnologyDetail[] };
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

function isCriticalTechnology(technologyName?: string): boolean {
  const criticalTechnologies = [
    'SSL',
    'TLS',
    'OAuth',
    'JWT',
    'WAF',
    'CDN',
    'DDoS Protection',
    'Firewall',
    'Antivirus',
    'IDS',
    'IPS',
  ];
  return technologyName ? criticalTechnologies.includes(technologyName) : false;
}

const getIconPath = (icon?: string): string => {
  if (!icon) return '/assets/icons/default.svg';
  const normalizedIcon = icon.trim();
  const lowerIcon = normalizedIcon.toLowerCase();
  const fileName = normalizedIcon.split('/').pop() || normalizedIcon;
  if (lowerIcon.endsWith('.png')) {
    return `/assets/icons/converted/${fileName}`;
  } else if (lowerIcon.endsWith('.svg')) {
    return `/assets/icons/${fileName}`;
  }
  return '/assets/icons/default.svg';
};

const Technologies: React.FC<TechnologiesProps> = ({
  selectedUrl,
  urls,
  onUrlChange,
  technologiesData,
}) => {
  const { t } = useTranslation();
  const categoriesArray = Object.entries(technologiesData) as [string, TechnologyDetail[]][];
  const categoriesChunks = chunkArray(categoriesArray, 2);

  const handleUrlChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUrlChange(e.target.value);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg space-y-6">
      {selectedUrl && (
        <div>
          <div className="flex items-center mb-6">
            <div className="w-3 h-10 bg-gray-800 mr-4"></div>
            <h3 className="text-4xl font-bold">{t('technologies.title')}</h3>
          </div>
          <div className="text-gray-700 text-xl mb-4">
            <p>{t('technologies.description')}</p>
          </div>
          <div className="mb-4">
            <select
              id="url-select"
              value={selectedUrl || ''}
              onChange={handleUrlChange}
              className="block w-full max-w-md p-2 border border-gray-800 bg-gray-800 text-white font-bold rounded-md shadow-sm"
            >
              {urls.map((url) => (
                <option key={url.id} value={url.url}>
                  {url.url}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {categoriesArray.length > 0 ? (
        categoriesChunks.map((chunk, index) => (
          <div key={index} className="flex flex-wrap -mx-2">
            {chunk.map(([categoryName, techs]) => (
              <div key={categoryName} className="w-full md:w-1/2 px-2 mb-4">
                <h4 className="text-2xl font-semibold mb-2">{categoryName}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {techs.map((tech, idx) => {
                    const iconPath = getIconPath(tech.icon);
                    return (
                      <div
                        key={idx}
                        className={`flex flex-col items-center p-4 rounded-lg ${
                          isCriticalTechnology(tech.name)
                            ? 'bg-yellow-100'
                            : 'bg-transparent hover:bg-gray-200'
                        }`}
                      >
                        <img
                          src={iconPath}
                          alt={tech.name || 'Tecnología'}
                          className="mb-2 h-16 w-16 object-contain"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/assets/icons/default.svg';
                          }}
                        />
                        <div className="flex items-center space-x-1">
                          <span className="text-center font-bold">{tech.name}</span>
                          {/* Icono de info con tooltip */}
                          <div className="relative group">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 text-gray-500 cursor-pointer"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20 10 10 0 010-20z"
                              />
                            </svg>
                            <div
                              className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10"
                              style={{ textAlign: 'justify' }}
                            >
                              {tech.description
                                ? tech.description
                                : t('technologies.descriptionNotAvailable')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))
      ) : (
        <p className="text-center text-gray-500">{t('technologies.no_technologies')}</p>
      )}

      {selectedUrl && (
        <div className="text-gray-700 text-xl">
          {categoriesArray.length > 0 ? (
            <p>{t('technologies.footer_detected')}</p>
          ) : (
            <p className="text-red-600">{t('technologies.footer_not_detected')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Technologies;
